/**
 * XHR-based Supabase Storage upload that exposes upload progress and abort.
 *
 * The supabase-js SDK's storage.upload() uses fetch() internally and does
 * not surface upload progress events or accept an AbortSignal. For
 * large mobile uploads (demo reels) we need both to give users feedback
 * and let them cancel — so we hit the storage REST endpoint directly.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

export type UploadProgress = { loaded: number; total: number; pct: number };

export type StorageXhrUploadOptions = {
  supabase: SupabaseClient;
  bucket: string;
  path: string;
  file: File;
  contentType?: string;
  cacheControl?: string;
  upsert?: boolean;
  signal?: AbortSignal;
  onProgress?: (p: UploadProgress) => void;
};

export type StorageXhrUploadResult = {
  status: number;
  statusText: string;
  body: any;
};

export class StorageUploadError extends Error {
  status: number;
  body: any;
  isNetwork: boolean;
  isAbort: boolean;
  constructor(opts: { message: string; status: number; body?: any; isNetwork?: boolean; isAbort?: boolean }) {
    super(opts.message);
    this.name = 'StorageUploadError';
    this.status = opts.status;
    this.body = opts.body;
    this.isNetwork = !!opts.isNetwork;
    this.isAbort = !!opts.isAbort;
  }
}

export async function storageUploadWithProgress({
  supabase,
  bucket,
  path,
  file,
  contentType,
  cacheControl = '3600',
  upsert = false,
  signal,
  onProgress,
}: StorageXhrUploadOptions): Promise<StorageXhrUploadResult> {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new StorageUploadError({
      message: 'Supabase URL is not configured.',
      status: 0,
    });
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) {
    throw new StorageUploadError({
      message: 'Your session may have expired. Refresh the page and try again.',
      status: 401,
    });
  }

  const url = `${supabaseUrl.replace(/\/$/, '')}/storage/v1/object/${encodeURIComponent(bucket)}/${path
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`;

  return new Promise<StorageXhrUploadResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(upsert ? 'PUT' : 'POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('x-upsert', upsert ? 'true' : 'false');
    xhr.setRequestHeader('Cache-Control', `max-age=${cacheControl}`);
    if (contentType) {
      xhr.setRequestHeader('Content-Type', contentType);
    }

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      onProgress?.({
        loaded: evt.loaded,
        total: evt.total,
        pct: Math.min(100, Math.round((evt.loaded / evt.total) * 100)),
      });
    };

    xhr.onload = () => {
      let parsed: any = null;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        parsed = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status, statusText: xhr.statusText, body: parsed });
      } else {
        reject(
          new StorageUploadError({
            message: parsed?.message || parsed?.error || xhr.statusText || `Upload failed (${xhr.status})`,
            status: xhr.status,
            body: parsed,
          }),
        );
      }
    };

    xhr.onerror = () => {
      reject(
        new StorageUploadError({
          message: 'Network error',
          status: 0,
          isNetwork: true,
        }),
      );
    };

    xhr.onabort = () => {
      reject(
        new StorageUploadError({
          message: 'Upload cancelled',
          status: 0,
          isAbort: true,
        }),
      );
    };

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(file);
  });
}

const ALLOWED_AUDIO_MIME_PREFIX = 'audio/';
const ALLOWED_VIDEO_MIME_PREFIX = 'video/';
const ALLOWED_AUDIO_EXTS = ['wav', 'mp3', 'm4a', 'aac', 'ogg', 'flac'];
const ALLOWED_VIDEO_EXTS = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'm4v'];

export const MAX_REEL_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

export type ReelKind = 'audio' | 'video';

export type ReelPreflight =
  | { ok: true }
  | { ok: false; code: 'empty' | 'too-large' | 'wrong-type'; message: string };

export function preflightReelFile(file: File, kind: ReelKind): ReelPreflight {
  if (!file || file.size === 0) {
    return {
      ok: false,
      code: 'empty',
      message:
        "Couldn't read the file. If you picked it from Google Drive or another cloud storage, download it to your device first, then upload.",
    };
  }
  if (file.size > MAX_REEL_SIZE_BYTES) {
    return {
      ok: false,
      code: 'too-large',
      message: 'File is too large. Maximum size is 100 MB. Try compressing or trimming the file.',
    };
  }

  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();
  const expectedPrefix = kind === 'audio' ? ALLOWED_AUDIO_MIME_PREFIX : ALLOWED_VIDEO_MIME_PREFIX;
  const allowedExts = kind === 'audio' ? ALLOWED_AUDIO_EXTS : ALLOWED_VIDEO_EXTS;

  // Mobile pickers (especially Drive on Android) sometimes report empty
  // or generic mime types — fall back to extension check in that case.
  const mimeOk = mime.startsWith(expectedPrefix);
  const extOk = allowedExts.includes(ext);
  if (!mimeOk && !extOk) {
    return {
      ok: false,
      code: 'wrong-type',
      message:
        kind === 'audio'
          ? 'Unsupported file type. Use MP3, WAV, M4A, AAC, OGG, or FLAC.'
          : 'Unsupported file type. Use MP4, MOV, AVI, WEBM, MKV, or M4V.',
    };
  }

  return { ok: true };
}

export function describeUploadError(err: unknown): string {
  if (err instanceof StorageUploadError) {
    if (err.isAbort) return 'Upload cancelled.';
    if (err.isNetwork) {
      return 'Upload failed — looks like a network problem. Check your connection and try again.';
    }
    if (err.status === 413) return 'File is too large for the server.';
    if (err.status === 401 || err.status === 403) {
      return 'Upload failed — your session may have expired. Refresh the page and try again.';
    }
    return err.message ? `Upload failed: ${err.message}` : 'Upload failed.';
  }
  const msg = err instanceof Error ? err.message : String(err ?? '');
  if (/failed to fetch|network|networkerror/i.test(msg)) {
    return 'Upload failed — looks like a network problem. Check your connection and try again.';
  }
  return msg ? `Upload failed: ${msg}` : 'Upload failed.';
}

export function reportUploadError(payload: {
  userId: string;
  reelType: ReelKind;
  fileName: string;
  fileType: string;
  fileSize: number;
  status: number;
  errorMessage: string;
  isAbort: boolean;
  isNetwork: boolean;
}) {
  const enriched = {
    type: 'upload-error',
    surface: 'demo-reel',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    ...payload,
  };
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(
        '/api/debug/client-error',
        new Blob([JSON.stringify(enriched)], { type: 'application/json' }),
      );
      return;
    }
  } catch {
    // fall through to fetch
  }
  try {
    void fetch('/api/debug/client-error', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(enriched),
    });
  } catch {
    // last resort
    // eslint-disable-next-line no-console
    console.error('[upload-error]', enriched);
  }
}
